import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, UserPlus, Loader2 } from "lucide-react";
import { ClientFormSheet } from "./ClientFormSheet";
import { initials, avatarColor, displayName } from "./clientConstants";

export function ClientPicker({ selected, onSelect, onClear, excludeIds = [], allowAddNew = true, excludeArchived = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open || !q.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/admin/clients/search", { params: { q, page: 1, page_size: 8, sort_by: "name_asc" } })
        .then((r) => setResults(r.data.items.filter((c) => !excludeIds.includes(c.id) && !(excludeArchived && c.status === "archived")))).catch(() => setResults([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (selected) {
    return (
      <div data-testid="order-client-selected" className="flex items-center gap-3 rounded-lg border border-[#E4E9F0] bg-[#F8FAFC] px-3 py-2.5">
        <span className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-xs font-semibold text-[#101828]" style={{ backgroundColor: avatarColor(selected.email) }}>{initials(displayName(selected))}</span>
        <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#101828] truncate">{displayName(selected)}</div><div className="text-xs text-[#475467] truncate">{selected.email}</div></div>
        <button type="button" data-testid="order-client-unlink" onClick={onClear} className="text-[#94A3B8] hover:text-[#475467]"><X size={16} /></button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input data-testid="order-client-search" value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Search existing clients by name, email, or phone…" className="pl-9 rounded-lg" />
      </div>
      {open && (
        <div data-testid="order-client-results" className="absolute z-30 mt-1 w-full rounded-xl border border-[#E4E9F0] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)] max-h-64 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 px-3 py-3 text-sm text-[#94A3B8]"><Loader2 size={14} className="animate-spin" /> Searching…</div>}
          {!loading && q.trim() && results.length === 0 && <div className="px-3 py-3 text-sm text-[#94A3B8]">No matching clients.</div>}
          {!loading && results.map((c) => (
            <button type="button" key={c.id} data-testid={`order-client-result-${c.id}`} onClick={() => { onSelect(c); setOpen(false); setQ(""); }} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F8FAFC]">
              <span className="h-8 w-8 shrink-0 rounded-full grid place-items-center text-xs font-semibold text-[#101828]" style={{ backgroundColor: avatarColor(c.email) }}>{initials(displayName(c))}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><div className="text-sm font-medium text-[#101828] truncate">{displayName(c)}</div>{c.status === "archived" && <span data-testid={`order-client-result-archived-${c.id}`} className="shrink-0 text-[10px] font-medium uppercase tracking-wide bg-[#FBF2E1] text-[#8A6529] rounded-full px-1.5 py-0.5">{c.merged_into_name ? `Merged into ${c.merged_into_name}` : "Archived"}</span>}</div>
                <div className="text-xs text-[#475467] truncate">{c.email}{c.company ? ` · ${c.company}` : ""}</div>
              </div>
            </button>
          ))}
          {allowAddNew && (
            <button type="button" data-testid="order-client-add-new" onClick={() => { setAdding(true); setOpen(false); }} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-[#0C2347] border-t border-[#E4E9F0] hover:bg-[#F8FAFC]">
              <UserPlus size={14} /> Add new client
            </button>
          )}
        </div>
      )}
      {adding && <ClientFormSheet client={null} onClose={() => setAdding(false)} onSaved={(client) => { setAdding(false); onSelect(client); }} />}
    </div>
  );
}
